<template>
  <!-- Правая панель тикета филиала (p4, p5) -->
  <aside class="ticket-panel">
    <!-- У любого открытого обращения-проблемы, включая «Новое» (canSolve) -->
    <v-btn
      v-if="solvable"
      depressed
      block
      height="48"
      class="ticket-panel__solve text-normalize"
      :loading="solving"
      @click="$emit('solve')"
    >
      <v-icon left size="18">
        mdi-check-circle-outline
      </v-icon>
      Проблема решена
    </v-btn>

    <section v-if="appeal.assignee" class="ticket-panel__section">
      <div class="ticket-panel__caption">
        {{ closed ? 'ОТВЕЧАЛ ВАМ' : 'ОТВЕЧАЕТ ВАМ' }}
      </div>
      <div class="ticket-panel__person">
        <support-avatar :src="appeal.assignee.avatar" :name="appeal.assignee.name" :size="44" />
        <div>
          <div class="ticket-panel__person-name">
            {{ appeal.assignee.name }}
          </div>
          <div class="ticket-panel__person-sub" :class="{ 'ticket-panel__person-sub--online': online }">
            {{ personSub }}
          </div>
        </div>
      </div>
    </section>

    <section class="ticket-panel__section">
      <div class="ticket-panel__caption">
        ОБ ОБРАЩЕНИИ
      </div>
      <dl class="ticket-panel__rows">
        <dt>Статус</dt>
        <dd :class="`ticket-panel__status--${tone}`">
          {{ status }}
        </dd>
        <dt>Категория</dt>
        <dd>{{ categoryName }}</dd>
        <template v-if="appeal.order">
          <dt>Заказ</dt>
          <dd>{{ orderText }}</dd>
        </template>
        <template v-if="branchText">
          <dt>Филиал</dt>
          <dd>{{ branchText }}</dd>
        </template>
        <!-- «Автор», а не «Создала»: пола автора в данных нет -->
        <template v-if="appeal.createdBy">
          <dt>Автор</dt>
          <dd>{{ authorText }}</dd>
        </template>
      </dl>
    </section>

    <section v-if="branchOpen.length" class="ticket-panel__section ticket-panel__section--last">
      <div class="ticket-panel__caption">
        ОТКРЫТЫЕ ОБРАЩЕНИЯ ФИЛИАЛА
      </div>
      <button
        v-for="item in branchOpen"
        :key="item.id"
        type="button"
        class="ticket-panel__tile"
        :class="`ticket-panel__tile--${toneOf(item)}`"
        @click="$emit('open', item)"
      >
        <span class="ticket-panel__tile-head">
          <support-avatar
            v-if="item.assignee"
            :src="item.assignee.avatar"
            :name="item.assignee.name"
            :size="20"
          />
          <span class="ticket-panel__tile-title">{{ tileTitle(item) }}</span>
          <span class="ticket-panel__tile-day">{{ day(item.createdAt) }}</span>
        </span>
        <span class="ticket-panel__tile-subject">{{ item.subject || 'Без темы' }}</span>
      </button>
    </section>

    <button type="button" class="ticket-panel__all" @click="$emit('all')">
      <v-icon size="20">
        mdi-storefront-outline
      </v-icon>
      <span class="ticket-panel__all-label">Все обращения филиала</span>
      <span class="ticket-panel__all-count">{{ branchTotal }}</span>
      <v-icon size="18">
        mdi-chevron-right
      </v-icon>
    </button>
  </aside>
</template>

<script>
  import SupportAvatar from '../SupportAvatar.vue'
  import { category } from '../form'
  import { clock, money, rowTime, shortDay } from '../format'
  import { canSolve, isClosed, panelStatus, statusView } from '../status'

  export default {
    name: 'TicketPanel',
    components: { SupportAvatar },
    props: {
      appeal: { type: Object, required: true },
      branch: { type: Object, default: null },
      branchOpen: { type: Array, default: () => [] },
      branchTotal: { type: Number, default: 0 },
      solving: { type: Boolean, default: false },
    },
    computed: {
      solvable () {
        return canSolve(this.appeal)
      },
      closed () {
        return isClosed(this.appeal)
      },
      online () {
        return !this.closed && this.appeal.assignee?.isOnline
      },
      personSub () {
        if (this.closed && this.appeal.resolvedAt) return `Поддержка FAKT · закрыл в ${clock(this.appeal.resolvedAt)}`

        return this.online ? 'Поддержка FAKT · на линии' : 'Поддержка FAKT'
      },
      status () {
        return panelStatus(this.appeal)
      },
      tone () {
        return statusView(this.appeal).tone
      },
      categoryName () {
        return category(this.appeal.category).short
      },
      // Номер введён текстом, пока CRM лежал: суммы нет.
      orderText () {
        const order = this.appeal.order

        return order.total != null ? `№${order.number} · ${money(order.total)}` : `№${order.number}`
      },
      branchText () {
        if (!this.branch) return null

        return [this.branch.address, this.branch.city].filter(Boolean).join(' · ')
      },
      authorText () {
        return `${this.appeal.createdBy.name} · ${rowTime(this.appeal.createdAt)}`
      },
    },
    methods: {
      toneOf (item) {
        return statusView(item).tone
      },
      tileTitle (item) {
        return item.assignee ? `${item.number} · ${item.assignee.name}` : `${item.number} · изучаем`
      },
      day (iso) {
        return shortDay(iso)
      },
    },
  }
</script>

<style scoped lang="less">
@import '../tokens.less';

.ticket-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 20px 24px 24px;
  overflow-y: auto;
  border: 1px solid @fakt-divider;
  border-radius: 8px;
  background: @fakt-white;
}

.ticket-panel__solve {
  // v-btn block в колоночном флексе растягивается по высоте (flex 1 0 auto) - держим 48
  flex: 0 0 auto !important;
  border-radius: 10px;
  font-size: 14px;
  color: @fakt-white !important;
  background-color: @fakt-green !important;
}

.ticket-panel__section {
  padding-bottom: 16px;
  border-bottom: 1px solid @fakt-divider;

  &--last {
    border-bottom: 0;
  }
}

.ticket-panel__caption {
  margin-bottom: 12px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.6px;
  color: @fakt-text-2;
}

.ticket-panel__person {
  display: flex;
  gap: 12px;
  align-items: center;
}

.ticket-panel__person-name {
  font-size: 14px;
  font-weight: 500;
  color: @fakt-text;
}

.ticket-panel__person-sub {
  font-size: 12px;
  color: @fakt-text-2;

  &--online {
    color: @fakt-green;
  }
}

.ticket-panel__rows {
  display: grid;
  grid-template-columns: 118px 1fr;
  row-gap: 12px;
  margin: 0;
  font-size: 13px;

  dt {
    color: @fakt-text-2;
  }

  dd {
    margin: 0;
    color: @fakt-text;
  }
}

.ticket-panel__status {
  &--new { color: @sup-new !important; }
  &--work { color: @sup-work !important; }
  &--wait { color: @sup-wait !important; }
  &--done { color: @sup-done !important; }
  &--muted,
  &--idea { color: @fakt-text-2 !important; }
}

.ticket-panel__tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 10px 14px;
  border-left: 3px solid @fakt-text-3;
  border-radius: 8px;
  text-align: left;
  background: @fakt-surface;

  & + & {
    margin-top: 8px;
  }

  &--new { border-left-color: @sup-new; }
  &--work { border-left-color: @sup-work; }
  &--wait { border-left-color: @sup-wait; }
}

.ticket-panel__tile-head {
  display: flex;
  gap: 8px;
  align-items: center;
}

.ticket-panel__tile-title {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: @fakt-text;
}

.ticket-panel__tile-day {
  font-size: 11px;
  color: @fakt-text-3;
}

.ticket-panel__tile-subject {
  overflow: hidden;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: @fakt-text-2;
}

.ticket-panel__all {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  align-items: center;
  height: 40px;
  margin-top: auto;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: @fakt-text;
  background: @fakt-surface;
}

.ticket-panel__all-label {
  flex: 1;
  text-align: left;
}

.ticket-panel__all-count {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 11px;
  line-height: 22px;
  text-align: center;
  color: @fakt-white;
  background: @fakt-dark;
}
</style>
